import React from 'react';
import {
  TextField,
} from '@mui/material';
import { useFormContext, Controller } from 'react-hook-form';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import { useTranslation } from 'react-i18next';
import HelpTextItem from 'components/menu-drawer/help-text-item';
import FormGridContainer from 'components/menu-drawer/form-grid-container';
import FormGridItem from 'components/menu-drawer/form-grid-item';
import TagChip from 'components/tag-chip';


const filter = createFilterOptions({
  limit: 40
});


export default function DeploymentEditTags({ options, drawerState, translation }) {
  const { t } = useTranslation(translation);
  const { control } = useFormContext();
  
  const tagSort = (a, b) => {
    // Sort default tags first, then alphabetically within each group
    if (a?.isDefault && !b?.isDefault) return -1;
    if (!a?.isDefault && b?.isDefault) return 1;
    if (a?.name?.toLowerCase() < b?.name?.toLowerCase()) return -1;
    if (a?.name?.toLowerCase() > b?.name?.toLowerCase()) return 1;
    return 0;
  }

  // Options come from backend, already with isDefault property
  const [tagOptions, setTagOptions] = React.useState(options.sort(tagSort));

  const addNewOption = (option) => {
    const exists = tagOptions.find(tag => 
      tag.name.toLowerCase() === option.name.toLowerCase()
    );
    if (!exists) {
      setTagOptions(prev => [...prev, option].sort(tagSort));
    }
  }

  return (
    <HelpTextItem
      sidebarText={drawerState.sidebarText}
      title={t('info.tags.helpTitle')}
      text={t('info.tags.helpInfo')}
    >
      {focusHandlers =>
        <FormGridContainer>
          <FormGridItem md={7}>
            <Controller
              control={control}
              name='tags'
              render={({ field }) => 
                <Autocomplete
                  multiple
                  autoHighlight
                  filterSelectedOptions
                  limitTags={3}
                  value={field.value || []}
                  options={tagOptions}
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(option, value) => 
                    option.name.toLowerCase() === value.name.toLowerCase()
                  }
                  onChange={(e, newValue) => {
                    const newTags = newValue?.map(tag => {
                      if (tag.inputValue) {
                        // New custom tag
                        const newTag = { name: tag.inputValue, isDefault: false };
                        addNewOption(newTag);
                        return newTag;
                      }
                      return tag;
                    });
                    field.onChange(newTags);
                  }}
                  renderInput={(params) => (
                    <TextField 
                      {...params} 
                      label={t('info.tags.label')}
                      variant="outlined" 
                      size="small"
                      placeholder="Type to add a new tag" 
                      onFocus={focusHandlers.onFocus}
                      onBlur={focusHandlers.onBlur}
                    />
                  )}
                  renderTags={(tagValue, getTagProps) =>
                    tagValue.map((option, index) => (
                      <TagChip
                        label={option.name}
                        {...getTagProps({ index })}
                      />
                    ))
                  }
                  filterOptions={(options, params) => {
                    const filtered = filter(options, params);
                    if (params.inputValue !== '' && 
                        !tagOptions.find(tag => 
                          tag.name.toLowerCase() === params.inputValue.toLowerCase()
                        )
                    ) {
                      filtered.push({
                        inputValue: params.inputValue,
                        name: `Add "${params.inputValue}"`,
                      });
                    }
                    return filtered;
                  }}
                />
              }
            />
          </FormGridItem>
        </FormGridContainer>
      }
    </HelpTextItem>
  );
}